import { useMemo } from 'react';

import { AnnotationsTrackConfig } from '../types/annotations-track-props.js';
import type { TextOverflowOptions } from '../types/marker.js';
import { type AnnotationsScale } from '../utils/calculate-marker-dimension.js';
import { processAnnotationTracksData } from '../utils/process-annotation-tracks-data.js';

/**
 *
 * @param tracks -
 * @param scale - used to derive the visible domain and to detect markers that
 * visually collide on canvas so they can be grouped into a shared tooltip
 * @param textOverflow - how range marker labels behave when they overflow
 * used together with `scale`
 * @param domain - optional explicit domain override used only for
 * out-of-bounds filtering (e.g. in TimeseriesChart, where the chart scale can
 * cover a wider time range than the annotation markers). `scale` is always
 * used for rendering positions and pixel-based visual-collision detection.
 * @returns
 */
export const useGroupedAnnotationsData = (
  tracks: AnnotationsTrackConfig[],
  scale?: AnnotationsScale,
  textOverflow?: TextOverflowOptions,
  domain?: Date[] | number[],
) =>
  useMemo(() => {
    const effectiveDomain = domain ?? scale?.domain();
    return tracks.map((track, index) => ({
      markers: processAnnotationTracksData(
        track,
        index,
        effectiveDomain,
        scale,
        textOverflow,
      ),
      label: track.label,
      hidden: track.hidden,
    }));
  }, [tracks, scale, textOverflow, domain]);
